import React from 'react';
import { Link } from 'react-router-dom';

// Export empty object to ensure file is treated as a module
export {};

import Layout from '../components/layout/Layout';

const Terms: React.FC = () => {
  return (
    <Layout>
      <div className="bg-gradient-to-br from-dark to-dark-dark text-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4 text-primary">Terms of Service</h1>
            <p className="text-xl max-w-3xl mx-auto">
              Please read these terms carefully before using Spoil Engine.
            </p>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Introduction</h2>
              <p className="text-gray-300 mb-4">Last Updated: April 12, 2025</p>
              <p className="text-gray-300 mb-4">
                Welcome to SpinDom. These Terms of Service (&quot;Terms&quot;) govern your use of
                our website, services, and applications (collectively, the &quot;Service&quot;). By
                accessing or using the Service, you agree to be bound by these Terms. If you
                disagree with any part of the Terms, you may not access the Service.
              </p>
              <p className="text-gray-300">
                We reserve the right to update or change these Terms at any time. We will provide
                notice of any changes by updating the &quot;Last Updated&quot; date. Your continued
                use of the Service following the posting of any changes constitutes acceptance of
                those changes.
              </p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Account Terms</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Account Creation</h3>
                  <p className="text-gray-300 mb-4">
                    To access certain features of the Service, you may be required to create an
                    account. You must provide accurate, current, and complete information during the
                    registration process and keep your account information up-to-date.
                  </p>
                  <p className="text-gray-300">
                    You are responsible for safeguarding the password that you use to access the
                    Service and for any activities or actions under your password. We encourage you
                    to use a strong, unique password for your account.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">Account Termination</h3>
                  <p className="text-gray-300 mb-4">
                    We reserve the right to suspend or terminate your account and refuse any and all
                    current or future use of the Service, for any reason at our sole discretion,
                    including breach of these Terms.
                  </p>
                  <p className="text-gray-300">
                    You may delete your account at any time through your account settings or by
                    contacting us. Upon termination, your right to use the Service will immediately
                    cease.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">Age Restrictions</h3>
                  <p className="text-gray-300">
                    The Service is intended for users who are at least 18 years of age. By using the
                    Service, you represent and warrant that you are at least 18 years old. If you
                    are under 18, you may not use the Service under any circumstances.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Service Terms</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Service Availability</h3>
                  <p className="text-gray-300 mb-4">
                    We strive to ensure that the Service is available at all times. However, we do
                    not guarantee that the Service will be available at all times without
                    interruption. We reserve the right to suspend or discontinue the Service at any
                    time, with or without notice.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">User Content</h3>
                  <p className="text-gray-300 mb-4">
                    Our Service allows you to create, upload, and share content, including wheel
                    configurations, entries, and customizations (&quot;User Content&quot;). You
                    retain all rights to your User Content, but you grant us a worldwide,
                    non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish,
                    and display such User Content in connection with providing and promoting the
                    Service.
                  </p>
                  <p className="text-gray-300 mb-4">
                    You are solely responsible for your User Content and the consequences of posting
                    or publishing it. By posting User Content, you represent and warrant that:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>
                      You own or have the necessary rights to use and authorize us to use your User
                      Content
                    </li>
                    <li>
                      Your User Content does not violate the privacy rights, publicity rights,
                      copyrights, contract rights, or any other rights of any person or entity
                    </li>
                    <li>
                      Your User Content does not contain material that is unlawful, obscene,
                      defamatory, threatening, pornographic, harassing, hateful, or otherwise
                      objectionable
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">Prohibited Uses</h3>
                  <p className="text-gray-300 mb-4">
                    You agree not to use the Service for any purpose that is illegal or prohibited
                    by these Terms. Prohibited uses include, but are not limited to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>
                      Using the Service for any illegal purpose or in violation of any local, state,
                      national, or international law
                    </li>
                    <li>Harassing, threatening, or intimidating other users</li>
                    <li>
                      Impersonating any person or entity, or falsely stating or otherwise
                      misrepresenting your affiliation with a person or entity
                    </li>
                    <li>
                      Interfering with or disrupting the Service or servers or networks connected to
                      the Service
                    </li>
                    <li>
                      Attempting to gain unauthorized access to the Service, other accounts, or
                      computer systems
                    </li>
                    <li>
                      Collecting or harvesting any information from the Service, including user
                      account information
                    </li>
                    <li>
                      Using the Service in a manner that could disable, overburden, damage, or
                      impair the Service
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">User Tiers</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Free Access</h3>
                  <p className="text-gray-300 mb-4">
                    Our Service offers two user tiers: Guest and Signed-In users. Both tiers are
                    completely free to use.
                  </p>
                  <p className="text-gray-300">
                    Guest users can access basic functionality without creating an account, while
                    Signed-In users gain access to enhanced features after creating a free account.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">Feature Access</h3>
                  <p className="text-gray-300 mb-4">
                    Some features of the Service are only available to Signed-In users. Creating an
                    account is free and provides immediate access to these enhanced features.
                  </p>
                  <p className="text-gray-300">
                    We reserve the right to modify the features available to each user tier at any
                    time. Your continued use of the Service after such changes constitutes your
                    acceptance of the new feature distribution.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Intellectual Property</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Service Content</h3>
                  <p className="text-gray-300 mb-4">
                    The Service and its original content (excluding User Content), features, and
                    functionality are and will remain the exclusive property of SpinDom and its
                    licensors. The Service is protected by copyright, trademark, and other laws of
                    both the United States and foreign countries.
                  </p>
                  <p className="text-gray-300">
                    Our trademarks and trade dress may not be used in connection with any product or
                    service without the prior written consent of SpinDom.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">Copyright Policy</h3>
                  <p className="text-gray-300 mb-4">
                    We respect the intellectual property rights of others and expect users of the
                    Service to do the same. We will respond to notices of alleged copyright
                    infringement that comply with applicable law.
                  </p>
                  <p className="text-gray-300">
                    If you believe that your copyrighted work has been copied in a way that
                    constitutes copyright infringement, please contact us with the following
                    information:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>A description of the copyrighted work that you claim has been infringed</li>
                    <li>
                      A description of where the material that you claim is infringing is located on
                      the Service
                    </li>
                    <li>
                      Your contact information, including your address, telephone number, and email
                      address
                    </li>
                    <li>
                      A statement by you that you have a good faith belief that the disputed use is
                      not authorized by the copyright owner, its agent, or the law
                    </li>
                    <li>
                      A statement by you, made under penalty of perjury, that the above information
                      in your notice is accurate and that you are the copyright owner or authorized
                      to act on the copyright owner&apos;s behalf
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Limitation of Liability</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Disclaimer of Warranties</h3>
                  <p className="text-gray-300 mb-4">
                    THE SERVICE IS PROVIDED ON AN &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot;
                    BASIS, WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT
                    NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
                    PURPOSE, OR NON-INFRINGEMENT.
                  </p>
                  <p className="text-gray-300">
                    WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED OR ERROR-FREE, THAT
                    DEFECTS WILL BE CORRECTED, OR THAT THE SERVICE OR THE SERVERS THAT MAKE IT
                    AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">Limitation of Liability</h3>
                  <p className="text-gray-300 mb-4">
                    IN NO EVENT SHALL SPINDOM, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS, BE
                    LIABLE TO YOU FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, PUNITIVE, OR
                    CONSEQUENTIAL DAMAGES WHATSOEVER RESULTING FROM ANY (I) ERRORS, MISTAKES, OR
                    INACCURACIES OF CONTENT, (II) PERSONAL INJURY OR PROPERTY DAMAGE, OF ANY NATURE
                    WHATSOEVER, RESULTING FROM YOUR ACCESS TO AND USE OF OUR SERVICE, (III) ANY
                    UNAUTHORIZED ACCESS TO OR USE OF OUR SECURE SERVERS AND/OR ANY AND ALL PERSONAL
                    INFORMATION AND/OR FINANCIAL INFORMATION STORED THEREIN, (IV) ANY INTERRUPTION
                    OR CESSATION OF TRANSMISSION TO OR FROM OUR SERVICE, (V) ANY BUGS, VIRUSES,
                    TROJAN HORSES, OR THE LIKE, WHICH MAY BE TRANSMITTED TO OR THROUGH OUR SERVICE
                    BY ANY THIRD PARTY, AND/OR (VI) ANY ERRORS OR OMISSIONS IN ANY CONTENT OR FOR
                    ANY LOSS OR DAMAGE OF ANY KIND INCURRED AS A RESULT OF YOUR USE OF ANY CONTENT
                    POSTED, EMAILED, TRANSMITTED, OR OTHERWISE MADE AVAILABLE VIA THE SERVICE,
                    WHETHER BASED ON WARRANTY, CONTRACT, TORT, OR ANY OTHER LEGAL THEORY, AND
                    WHETHER OR NOT THE COMPANY IS ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                  </p>
                  <p className="text-gray-300">
                    THE FOREGOING LIMITATION OF LIABILITY SHALL APPLY TO THE FULLEST EXTENT
                    PERMITTED BY LAW IN THE APPLICABLE JURISDICTION.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-400 mb-4">
              By using our Service, you acknowledge that you have read and understand these Terms of
              Service.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/privacy" className="btn-secondary px-8 py-3 text-lg">
                View Privacy Policy
              </Link>
              <Link to="/contact" className="btn-outline px-8 py-3 text-lg">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Terms;
